import {
  AppstoreAddOutlined,
  BankOutlined,
  BarChartOutlined,
  BranchesOutlined,
  CloseCircleOutlined,
  DoubleRightOutlined,
  EyeInvisibleOutlined,
  FlagOutlined,
  HomeOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  PartitionOutlined,
  QuestionCircleOutlined,
  SafetyOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useQuery, useSuspenseQuery } from '@apollo/client'
import { FetchMeDocument, FetchSourcesTableRecordsDocument } from '@salescore/client-api'
import { CONSTANT, Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { checkVisualizeEnabled, HandleQuery, Logo, useCanForFeatures } from '@salescore/client-common'
import { SelectUser } from '@salescore/client-feature-setting'
// eslint-disable-next-line sonarjs/deprecation
import { organizationHasFeature, useModal } from '@salescore/frontend-common'
import { Link, useMatches, useNavigate } from '@tanstack/react-router'
import { Layout, Menu, type MenuProps, Modal, Space } from 'antd'
import { Array } from 'effect'
import type { ReactElement, ReactNode } from 'react'
import { Trans } from 'react-i18next'

import type { FileRoutesById } from '../../routeTree.gen.ts'
import { SiderOrganizationV2 } from './SiderOrganizationV2.tsx'
import { SiderUserV2 } from './SiderUserV2.tsx'

const Divider = (): ReactNode => <hr className="m-0 bg-white opacity-30"></hr>

const SourceSettingsLabel = ({ organizationId }: { organizationId: string }): ReactNode => {
  const { data } = useQuery(FetchSourcesTableRecordsDocument, {
    variables: {
      organizationId,
    },
    fetchPolicy: 'cache-and-network',
  })
  const hasError = data?.sourcesTableRecords.some((c) => c.hasError) ?? false
  return (
    <Space>
      <Trans>連携先一覧</Trans>
      {hasError && <CloseCircleOutlined className="text-red-500" />}
    </Space>
  )
}

type GroupKey = 'connections' | 'help' | 'other_organization' | 'sign_out' | 'kpi' | 'users_and_groups' | 'permissions'
type RouteItems = Array<Required<MenuProps>['items'][number] & { key: keyof FileRoutesById | GroupKey }>

const buildConnectionMenuItems = ({
  ability,
  organizationId,
  navigate,
}: {
  ability: { canManageConnection: boolean; canManageEltModel: boolean }
  organizationId: string
  navigate: ReturnType<typeof useNavigate>
}): RouteItems => {
  const children: RouteItems = [
    ...(ability.canManageConnection
      ? [
          {
            label: <SourceSettingsLabel organizationId={organizationId} />,
            key: `/o/$organizationId/v2/settings/sources/` as const,
            icon: <AppstoreAddOutlined />,
            onClick: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/sources', params: { organizationId } })
            },
          },
        ]
      : []),
    ...(ability.canManageEltModel
      ? [
          {
            label: <Trans>オブジェクト一覧</Trans>,
            key: `/o/$organizationId/v2/settings/elt_models/` as const,
            icon: <UnorderedListOutlined />,
            onClick: (): void => {
              void navigate({ to: '/o/$organizationId/v2/settings/elt_models', params: { organizationId } })
            },
          },
        ]
      : []),
  ]
  return Array.isArrayNonEmpty(children)
    ? [
        {
          label: <Trans>連携設定</Trans>,
          key: 'connections',
          icon: <BranchesOutlined />,
          children,
        },
      ]
    : []
}

// feature flag が閉じる 3 点 (設定 UI の表示 / 直接 URL 遷移 / 書き込み mutation) のうち「設定 UI の表示」をここで閉じる
const buildPermissionMenuItems = ({
  hasRecordVisibilityFeature,
  canManageRecordVisibility,
  organizationId,
  navigate,
}: {
  hasRecordVisibilityFeature: boolean
  canManageRecordVisibility: boolean
  organizationId: string
  navigate: ReturnType<typeof useNavigate>
}): RouteItems => {
  if (!hasRecordVisibilityFeature || !canManageRecordVisibility) {
    return []
  }
  return [
    {
      label: <Trans>権限設定</Trans>,
      key: `permissions`,
      icon: <SafetyOutlined />,
      children: [
        {
          label: <Trans>既定公開範囲</Trans>,
          key: `/o/$organizationId/v2/settings/record_visibilities` as const,
          icon: <EyeInvisibleOutlined />,
          onClick: (): void => {
            void navigate({ to: '/o/$organizationId/v2/settings/record_visibilities', params: { organizationId } })
          },
        },
      ],
    },
  ]
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function SiderV2({ organizationId }: { organizationId: string }): ReactElement {
  const matches = useMatches()
  const match = matches.at(-1)

  // eslint-disable-next-line sonarjs/deprecation
  const selectUserModal = useModal()
  const ability = useCanForFeatures()
  const navigate = useNavigate()

  const {
    data: { myUser },
  } = useSuspenseQuery(FetchMeDocument, {
    variables: { organizationId },
    fetchPolicy: 'cache-first',
  })

  const { identity: myIdentity } = myUser

  const items: RouteItems = [
    {
      label: <Trans>ホーム</Trans>,
      key: '/o/$organizationId/v2/',
      onClick: (): void => {
        void navigate({ to: '/o/$organizationId/v2', params: { organizationId } })
      },
      icon: <HomeOutlined />,
    },
    {
      label: <Trans>個人設定</Trans>,
      key: `/o/$organizationId/v2/settings/me`,
      icon: <UserOutlined />,
      onClick: (): void => {
        void navigate({ to: '/o/$organizationId/v2/settings/me', params: { organizationId } })
      },
    },
  ]

  if (ability.canManageOrganizationSetting) {
    items.push({
      label: <Trans>組織設定</Trans>,
      key: `/o/$organizationId/v2/settings/`,
      icon: <BankOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings', params: { organizationId } })
      },
    })
  }
  items.push(...buildConnectionMenuItems({ ability, organizationId, navigate }))

  const userRelatedItems: RouteItems = []
  if (ability.canManageUsers) {
    userRelatedItems.push({
      label: <Trans>ユーザー一覧</Trans>,
      key: `/o/$organizationId/v2/settings/users`,
      icon: <UserOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/users', params: { organizationId } })
      },
    })
  }
  if (ability.canManageUserGroups) {
    userRelatedItems.push({
      label: <Trans>グループ一覧</Trans>,
      key: `/o/$organizationId/v2/settings/user_groups/`,
      icon: <TeamOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/user_groups', params: { organizationId } })
      },
    })
  }
  if (ability.canManageInvitation) {
    userRelatedItems.push({
      label: <Trans>ユーザー招待</Trans>,
      key: `/o/$organizationId/v2/settings/invitations`,
      icon: <MailOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/invitations', params: { organizationId } })
      },
    })
  }
  if (ability.canManagePolicy) {
    userRelatedItems.push({
      label: <Trans>権限セット一覧</Trans>,
      key: `/o/$organizationId/v2/settings/policies`,
      icon: <LockOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/policies', params: { organizationId } })
      },
    })
  }
  if (Array.isArrayNonEmpty(userRelatedItems)) {
    items.push({
      label: <Trans>ユーザー設定</Trans>,
      key: `users_and_groups`,
      icon: <TeamOutlined />,
      children: userRelatedItems,
    })
  }

  items.push(
    ...buildPermissionMenuItems({
      hasRecordVisibilityFeature: organizationHasFeature(myUser.organization, `enable_salesforce_record_permission`),
      canManageRecordVisibility: ability.canManageRecordVisibility,
      organizationId,
      navigate,
    }),
  )

  const goalRelatedItems: RouteItems = []
  if (ability.canManageGoal) {
    goalRelatedItems.push(
      {
        label: <Trans>目標設定</Trans>,
        key: `/o/$organizationId/v2/settings/goal_configs/`,
        icon: <FlagOutlined />,
        onClick: () => {
          void navigate({ to: '/o/$organizationId/v2/settings/goal_configs', params: { organizationId } })
        },
      },
      // 2023/01/25 サイドバーには不要なのでは？という話をした。
      // {
      //   label: '目標カテゴリ',
      //   key: `/o/$organizationId/v2/settings/goal_dimensions`,
      //   icon: <PartitionOutlined />,
      //   onClick: () => {
      //     redirect(routes.goalDimensionsPathV2())
      //   },
      // },
    )
  }
  if (ability.canManageDimensionGroup) {
    goalRelatedItems.push({
      label: <Trans>軸グループ設定</Trans>,
      key: `/o/$organizationId/v2/settings/dimension_groups`,
      icon: <PartitionOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/dimension_groups', params: { organizationId } })
      },
    })
  }
  if (Array.isArrayNonEmpty(goalRelatedItems) && checkVisualizeEnabled(myUser)) {
    items.push({
      label: <Trans>目標と集計軸</Trans>,
      key: `kpi`,
      icon: <FlagOutlined />,
      children: goalRelatedItems,
    })
  }

  if (ability.canReadUserActivity) {
    items.push({
      label: <Trans>利用状況</Trans>,
      key: `/o/$organizationId/v2/settings/user_activities`,
      icon: <BarChartOutlined />,
      onClick: () => {
        void navigate({ to: '/o/$organizationId/v2/settings/user_activities', params: { organizationId } })
      },
    })
  }

  items.push(
    {
      label: <Trans>ヘルプ</Trans>,
      key: 'help',
      icon: <QuestionCircleOutlined />,
      onClick: () => {
        Posthog.track(POSTHOG_EVENTS.click_help, {})
        window.open(CONSTANT.link.helpV2)
      },
    },
    {
      label: <Trans>ログアウト</Trans>,
      key: '/sign_out',
      onClick: () => {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: admin側と同じ処理が必要かも
        void navigate({ to: '/sign_out' })
      },
      icon: <LogoutOutlined />,
    },
  )

  if (myIdentity.users.length > 5) {
    items.push({
      label: <Trans>他の組織へ</Trans>,
      key: 'other_organization',
      icon: <DoubleRightOutlined />,
      onClick: () => {
        selectUserModal.showModal()
      },
    })
  }

  return (
    <HandleQuery>
      <Layout.Sider
        theme="light"
        style={{ fontWeight: 'bold', height: '100%' }}
        width={245}
        // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- dispaly-none-print は main.less にスタイル定義あり
        className="dispaly-none-print overflow-hidden"
      >
        <div className="px-4 py-5 text-center">
          <Link to="/o/$organizationId/v2" params={{ organizationId }}>
            <Logo type="black" />
          </Link>
        </div>
        <Divider />
        <Menu
          items={items}
          defaultSelectedKeys={[match?.routeId ?? '']}
          selectedKeys={[match?.routeId ?? '']}
          mode="inline"
          theme="light"
          style={{
            paddingTop: 12,
            paddingBottom: 30,
            height: 'calc(100% - 170px)',
            overflow: 'scroll',
          }}
        />

        {/* footer */}
        <div className="absolute z-10 w-full bg-white" style={{ bottom: 0 }}>
          <Divider />
          <SiderOrganizationV2 myUser={myUser} myIdentity={myIdentity} />
          <Divider />
          <SiderUserV2 myUser={myUser} />
        </div>

        <Modal
          open={selectUserModal.isModalVisible}
          onCancel={selectUserModal.hideModal}
          width={1000}
          okButtonProps={{ style: { display: 'none' } }}
          cancelButtonProps={{ style: { display: 'none' } }}
          style={{ top: '3%' }}
        >
          <SelectUser myIdentity={myIdentity} />
        </Modal>
      </Layout.Sider>
    </HandleQuery>
  )
}
